import { MouseEvent, useState } from 'react';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Checkbox from '@mui/material/Checkbox';
import TableSortLabel from '@mui/material/TableSortLabel';
import Box from '@mui/material/Box';
import { visuallyHidden } from '@mui/utils';
import Paper from '@mui/material/Paper';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import StarIcon from '@mui/icons-material/Star';
import Button from '@mui/material/Button';
import { Link } from 'react-router-dom';
import { getComparator, humanizeOwnerName } from '../../utils';
import { TagsCollection } from '../TagsCollection/TagsCollection';
import { DescriptionDialog } from './DescriptionDialog/DescriptionDialog';
import { CustomTablePagination } from '../CustomTablePagination/CustomTablePagination';
import { StarCheckbox } from '../StarCheckbox';

type TableSortOrder = 'asc' | 'desc';

export interface TableData {
  name: string;
  kind: string;
  owner: string;
  description?: string;
  type?: string;
  tags?: string[];
  targets?: string;
}

export type EntityTableProps = {
  headCells: HeadCell[];
  totalItems: number;
  limit: number;
  page: number;
  formattedItems: any[];
  handleChangePage: (newPage: number) => void;
  handleChangeRowsPerPage: (itemsPerPage: number) => void;
  allowedEntitiesPerPage: number[];
};

export interface HeadCell {
  id: keyof TableData;
  label: string;
  width?: string;
}

interface EnhancedTableProps {
  onRequestSort: (
    event: MouseEvent<unknown>,
    property: keyof TableData,
  ) => void;
  order: TableSortOrder;
  orderBy: string;
  headCells: HeadCell[];
}

function EnhancedTableHead(props: Readonly<EnhancedTableProps>) {
  const { order, orderBy, onRequestSort, headCells } = props;

  const createSortHandler =
    (property: keyof TableData) => (event: MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  const getCellStyle = (cell: HeadCell) => {
    if (cell.width && cell.width !== '') {
      return { width: cell.width };
    }

    return {};
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            sx={{ pointerEvents: 'none' }}
            icon={<StarIcon />}
          />
        </TableCell>
        {headCells?.map(headCell => (
          <TableCell
            key={headCell.id}
            padding="none"
            sortDirection={orderBy === headCell.id ? order : false}
            style={getCellStyle(headCell)}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

export function EntityTable({
  headCells,
  totalItems,
  limit,
  page,
  formattedItems,
  allowedEntitiesPerPage,
  handleChangePage,
  handleChangeRowsPerPage,
}: Readonly<EntityTableProps>) {
  const [order, setOrder] = useState<TableSortOrder>('asc');
  const [orderBy, setOrderBy] = useState<keyof TableData>('name');

  const handleRequestSort = (property: keyof TableData) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ width: '100%' }}>
        <Paper sx={{ width: '100%', mb: 2 }}>
          <TableContainer>
            <Table
              sx={{
                tableLayout: 'fixed',
              }}
            >
              <EnhancedTableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={(_, property) => handleRequestSort(property)}
                headCells={headCells}
              />
              <TableBody>
                {formattedItems
                  .slice()
                  ?.sort(getComparator(order, orderBy))
                  .map((item: any, index: number) => {
                    const labelId = `enhanced-table-checkbox-${index}`;

                    return (
                      <TableRow
                        hover
                        tabIndex={-1}
                        key={`row-${item.name}-${index}`}
                        data-testid={`table-row-${item.name}-${index}`}
                        sx={{ height: '80px', maxHeight: '80px' }}
                      >
                        <TableCell
                          padding="checkbox"
                          key={`checkbox-${item.name}-${index}`}
                          data-testid={`table-cell-checkbox-${item.name}-${index}`}
                        >
                          <StarCheckbox
                            kind={item.kind}
                            name={item.name}
                            namespace={item.namespace}
                          />
                        </TableCell>
                        {headCells.some(headCell => headCell.id === 'name') && (
                          <TableCell
                            key={`name-${item.name}-${index}`}
                            component="th"
                            id={labelId}
                            scope="row"
                            padding="none"
                            data-testid={`table-cell-${item.name}-${index}`}
                          >
                            {item?.name ? (
                              <Button
                                sx={{
                                  fontSize: '14px',
                                  fontWeight: '700',
                                  justifyContent: 'flex-start',
                                  ml: 1,
                                  '&:hover': {
                                    color: 'inherit',
                                    backgroundColor: 'transparent',
                                    textDecoration: 'underline',
                                  },
                                }}
                                component={Link}
                                to={item.uri}
                              >
                                {item?.title ?? item?.name}
                              </Button>
                            ) : null}
                          </TableCell>
                        )}
                        {headCells.some(headCell => headCell.id === 'kind') && (
                          <TableCell
                            key={`kind-${item.kind}-${index}`}
                            component="th"
                            id={labelId}
                            scope="row"
                            padding="none"
                            data-testid={`table-cell-${item.kind}-${index}`}
                          >
                            {item.kind || ''}
                          </TableCell>
                        )}
                        {headCells.some(headCell => headCell.id === 'type') && (
                          <TableCell
                            key={`type-${item.type}-${index}`}
                            sx={{ paddingLeft: '0' }}
                            data-testid={`table-cell-${item.type}-${index}`}
                          >
                            {item.type || ''}
                          </TableCell>
                        )}
                        {headCells.some(
                          headCell => headCell.id === 'owner',
                        ) && (
                          <TableCell
                            key={`owner-${item.owner}-${index}`}
                            sx={{ paddingLeft: '0' }}
                            data-testid={`table-cell-${item.owner}-${index}`}
                          >
                            {item?.owner &&
                              humanizeOwnerName(item?.owner || '')}
                          </TableCell>
                        )}
                        {headCells.some(headCell => headCell.id === 'tags') && (
                          <TableCell
                            key={`tags-${item.tags}-${index}`}
                            sx={{ paddingLeft: '0' }}
                            data-testid={`table-cell-tags-${index}`}
                          >
                            {item.tags ? (
                              <TagsCollection
                                tags={item.tags}
                                maxVisibleTags={2}
                                chipSize="small"
                              />
                            ) : (
                              ''
                            )}
                          </TableCell>
                        )}
                        {headCells.some(
                          headCell => headCell.id === 'description',
                        ) && (
                          <TableCell
                            key={`description-${item.description.slice(0, 7).split('').join('-')}-${index}`}
                            data-testid={`table-cell-description-${index}`}
                            sx={{
                              paddingLeft: '0',
                              paddingRight: '0',
                            }}
                          >
                            {item.description ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'center',
                                }}
                              >
                                <DescriptionDialog
                                  description={item.description}
                                  data-testid="description-dialog"
                                  index={index}
                                />
                              </Box>
                            ) : (
                              ''
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row-reverse',
          }}
        >
          <CustomTablePagination
            page={page}
            rowsPerPage={limit}
            rowsPerPageOptions={allowedEntitiesPerPage}
            count={totalItems ?? 0}
            onPageChange={newPage => handleChangePage(newPage)}
            onRowsPerPageChange={handleChangeRowsPerPage}
            data-testid="table-pagination"
          />
        </Box>
      </Box>
    </Box>
  );
}
